// src/services/firebase.js
// ✅ Unified helpers + retries + connection health + map-based groups
// Consistent with updated credit/paywall/group flows and safe merges for autosave.

import { useCallback, useMemo } from "react";
import { useAuth } from "../components/auth/auth-components";
import SportsDataService from "./sportsdata-service.js";
import { shouldSaveGameData, calculateCompletion } from "../utils/completion.js";
import { logger } from "../utils/logger";
import { getFootballSeasonYear } from "../../utils/season";

// -----------------------------
// Retry + error helpers
// -----------------------------
export const withRetry = async (operation, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      // Do not retry on permanent/auth/permission/arg errors
      const code = error?.code || "";
      if (
        code.includes("auth/") ||
        code.includes("permission-denied") ||
        code.includes("invalid-argument")
      ) {
        throw error;
      }
      if (i === maxRetries - 1) throw error;
      await new Promise((r) => setTimeout(r, delay * Math.pow(2, i)));
    }
  }
};

export const categorizeFirebaseError = (error) => {
  if (!error?.code)
    return error?.message?.toLowerCase().includes("network") ? "network" : "unknown";
  if (error.code.includes("auth/")) return "auth";
  if (error.code.includes("firestore/")) return "firestore";
  if (error.code.includes("storage/")) return "storage";
  if (error.code.includes("unavailable")) return "network";
  if (error.code.includes("deadline-exceeded")) return "timeout";
  if (error.code.includes("permission-denied")) return "permission";
  return "unknown";
};

// -----------------------------
// Hook
// -----------------------------
export const useFirebase = () => {
  // Auth (safe)
  let user = null;
  try {
    const auth = useAuth();
    user = auth?.user || null;
  } catch {
    user = null;
  }

  const sportsDataService = useMemo(() => new SportsDataService(), []);

  const generateInviteCode = useCallback(() => {
    return Array(6)
      .fill(0)
      .map(() =>
        "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789".charAt(Math.floor(Math.random() * 36))
      )
      .join("");
  }, []);

  // -----------------------------
  // Save / Load / Submit / List
  // -----------------------------
  const saveGameData = useCallback(
    async (gameData) => {
      try {
        // 🔒 Guard: never save read-only or fully submitted sessions as new docs
        if (
          (gameData?.status === "submitted" || gameData?._readOnly) &&
          !gameData?._existingDocId
        ) {
          logger.debug(
            "⏭️ Skipping save for submitted/read-only session without existingDocId"
          );
          return null;
        }

        logger.debug("💾 Auto-save start", {
          team: gameData?.team,
          status: gameData?.status,
          userId: user?.uid,
        });

        if (!gameData || typeof gameData !== "object") {
          const e = new Error("Invalid game data provided");
          e.code = "invalid-argument";
          throw e;
        }
        if (!shouldSaveGameData(gameData, user)) return null;

        const performSave = async () => {
          const currentCompletion = calculateCompletion(gameData);

          const cleanGameData = {
            ...gameData,
            team: String(gameData.team || ""),
            teamId: String(gameData.teamId || ""),
            participantName: String(gameData.participantName || ""),
            season: Number(gameData.season) || getFootballSeasonYear(),
            lastUpdated: new Date().toISOString(),
            dataSource: "SportsData.io",
            userId: user?.uid || gameData.userId || "anonymous",
            completionPercentage: currentCompletion.completionPercentage,
            status:
              gameData.status ||
              (currentCompletion.completionPercentage > 0 ? "in_progress" : "draft"),
            positionGrades: gameData.positionGrades || {},
            coachGrades: gameData.coachGrades || {},
            predictions:
              gameData.predictions || {
                gameResults: {},
                topPlayer: {},
                conferenceCoachRankings: {},
              },
            fanType: gameData.fanType || "expert",
            groupId: gameData.groupId || null,
            groupName: gameData.groupName || "",
            isGroupCreator: !!gameData.isGroupCreator,
            createdAt: gameData.createdAt || new Date().toISOString(),
            updatedCount: (gameData.updatedCount || 0) + 1,
          };

          // Firestore path
          if (typeof window !== "undefined" && window.firebase && window.db && user) {
            // Prefer to update an existing doc if the referenced id exists
            const preferredExistingId =
              gameData._existingDocId || gameData.id || gameData.docId;

            if (preferredExistingId) {
              try {
                const existingRef = window.db
                  .collection("gameAnalysis")
                  .doc(preferredExistingId);
                const existingSnap = await existingRef.get();
                if (existingSnap.exists) {
                  // Sanitize update payload
                  const sanitizedUpdate = {};
                  Object.entries(cleanGameData).forEach(([k, v]) => {
                    if (
                      v !== undefined &&
                      v !== null &&
                      typeof v !== "function" &&
                      !k.startsWith("_") &&
                      k !== "constructor"
                    ) {
                      sanitizedUpdate[k] = v;
                    }
                  });
                  await existingRef.set(sanitizedUpdate, { merge: true });
                  logger.debug("✅ Updated existing game (defensive merge)", {
                    docId: preferredExistingId,
                  });
                  return preferredExistingId;
                }
              } catch (e) {
                logger.warn(
                  "⚠️ Existing doc check failed, will fall back to create if needed",
                  e?.message
                );
              }
            }

            // Legacy flag path (still supported)
            const existingDocId =
              gameData._existingDocId || gameData.id || gameData.docId;
            if (existingDocId && (gameData._updateExisting || gameData._continuingExisting)) {
              const docRef = window.db.collection("gameAnalysis").doc(existingDocId);
              const snap = await docRef.get();
              if (snap.exists) {
                const sanitized = {};
                Object.entries(cleanGameData).forEach(([k, v]) => {
                  if (
                    v !== undefined &&
                    v !== null &&
                    typeof v !== "function" &&
                    !k.startsWith("_") &&
                    k !== "constructor"
                  ) {
                    sanitized[k] = v;
                  }
                });
                await docRef.set(sanitized, { merge: true });
                logger.debug("✅ Updated existing game (flag path)", {
                  docId: existingDocId,
                });
                return existingDocId;
              } else {
                logger.warn("⚠️ existingDocId not found, creating new");
              }
            }

            // ➕ Create a new document (only when we truly don't have/update an existing one)
            const sanitized = {};
            Object.entries(cleanGameData).forEach(([k, v]) => {
              if (
                v !== undefined &&
                v !== null &&
                typeof v !== "function" &&
                !k.startsWith("_") &&
                k !== "constructor"
              ) {
                sanitized[k] = v;
              }
            });

            const newDocRef = await window.db.collection("gameAnalysis").add(sanitized);
            logger.debug("✅ NEW game saved", {
              docId: newDocRef.id,
              completion: sanitized.completionPercentage,
            });
            return newDocRef.id;
          }

          // localStorage fallback
          const existingLocalId = gameData._existingDocId || gameData.id;
          const key = existingLocalId || `footballAnalysisData_${Date.now()}`;
          localStorage.setItem(key, JSON.stringify(cleanGameData));
          localStorage.setItem(
            "footballAnalysisData_latest",
            JSON.stringify(cleanGameData)
          );
          logger.debug("✅ Saved to localStorage fallback", { key });
          return key;
        };

        return await withRetry(performSave, 3, 1000);
      } catch (error) {
        console.error("❌ Auto-save error:", error);
        // Emergency local fallback
        try {
          const currentCompletion = calculateCompletion(gameData);
          const fallback = {
            team: gameData?.team || "",
            season: gameData?.season || getFootballSeasonYear(),
            positionGrades: gameData?.positionGrades || {},
            coachGrades: gameData?.coachGrades || {},
            predictions: gameData?.predictions || {},
            status: gameData?.status || "in_progress",
            completionPercentage: currentCompletion.completionPercentage,
            lastUpdated: new Date().toISOString(),
            fallbackSave: true,
            participantName: gameData?.participantName || "",
            fanType: gameData?.fanType || "expert",
          };
          const key = `footballAnalysisData_fallback_${Date.now()}`;
          localStorage.setItem(key, JSON.stringify(fallback));
          logger.debug("✅ Emergency localStorage save", { key });
          return key;
        } catch (fallbackError) {
          console.error("❌ Fallback save failed:", fallbackError);
          throw new Error("All save methods failed");
        }
      }
    },
    [user]
  );

  const loadGameData = useCallback(
    async (gameId) => {
      try {
        const performLoad = async () => {
          if (typeof window !== "undefined" && window.firebase && window.db && user) {
            const doc = await window.db.collection("gameAnalysis").doc(gameId).get();
            if (doc.exists) {
              const data = doc.data();
              const savedCompletion =
                data.completionPercentage || data.submissionCompletionPercentage;
              const current = calculateCompletion(data);
              return {
                ...data,
                id: gameId,
                loadedFrom: "firebase",
                completionPercentage: savedCompletion ?? current.completionPercentage,
              };
            }
          }
          // localStorage fallback
          const keys = ["footballAnalysisData_latest", "footballAnalysisData", gameId];
          for (const k of keys) {
            const raw = localStorage.getItem(k);
            if (raw) {
              const data = JSON.parse(raw);
              const savedCompletion =
                data.completionPercentage || data.submissionCompletionPercentage;
              const current = calculateCompletion(data);
              return {
                ...data,
                id: gameId || k,
                loadedFrom: "localStorage",
                completionPercentage: savedCompletion ?? current.completionPercentage,
              };
            }
          }
          return null;
        };
        return await withRetry(performLoad, 2, 1000);
      } catch (e) {
        console.error("❌ Load error:", e);
        throw e;
      }
    },
    [user]
  );

  const submitAnalysis = useCallback(
    async (analysisData) => {
      try {
        const perform = async () => {
          if (!analysisData) throw new Error("No analysis data provided");
          const submissionCompletion = calculateCompletion(analysisData);
          const update = {
            ...analysisData,
            status: "submitted",
            submittedAt: new Date().toISOString(),
            submittedBy: user?.uid,
            submissionTimestamp: Date.now(),
            lastUpdated: new Date().toISOString(),
            completionPercentage: submissionCompletion.completionPercentage,
            submissionCompletionPercentage: submissionCompletion.completionPercentage,
            isCompleted: true,
            isSubmitted: true,
          };

          if (typeof window !== "undefined" && window.firebase && window.db) {
            // Prefer updating the existing analysis doc when available
            const existingId =
              analysisData._existingDocId || analysisData.id || analysisData.docId;

            let ref;
            if (existingId) {
              ref = window.db.collection("gameAnalysis").doc(existingId);
            } else {
              const season = update.season || getFootballSeasonYear();
              const safeTeam = (update.team || "unknown").replace(/[^a-zA-Z0-9]/g, "_");
              const safeName = (update.participantName || "player").replace(
                /[^a-zA-Z0-9]/g,
                "_"
              );
              const derivedId = update.groupId
                ? `group_${update.groupId}_${safeTeam}_${season}_${safeName}`.replace(
                    /[^a-zA-Z0-9_-]/g,
                    "_"
                  )
                : `solo_${safeTeam}_${season}_${safeName}_${user?.uid?.slice(0, 8)}`.replace(
                    /[^a-zA-Z0-9_-]/g,
                    "_"
                  );
              ref = window.db.collection("gameAnalysis").doc(derivedId);
            }

            await ref.set(update, { merge: true });

            // Tiny verify delay for eventual consistency
            await new Promise((r) => setTimeout(r, 600));
            const verify = await ref.get();
            if (!verify.exists || verify.data()?.status !== "submitted") {
              throw new Error("Status update failed");
            }
            return verify.data();
          }
          throw new Error("Firebase not available");
        };
        return await withRetry(perform, 3, 1000);
      } catch (e) {
        console.error("❌ Submit error:", e);
        throw e;
      }
    },
    [user]
  );

  const getUserAnalyses = useCallback(
    async () => {
      try {
        if (typeof window !== "undefined" && window.firebase && window.db && user) {
          const snap = await window.db
            .collection("gameAnalysis")
            .where("userId", "==", user.uid)
            .get();
          const items = [];
          snap.forEach((doc) => {
            const data = doc.data();
            if (!data || typeof data !== "object") return;
            const savedCompletion =
              data.completionPercentage || data.submissionCompletionPercentage;
            const current = calculateCompletion(data);
            items.push({
              id: doc.id,
              ...data,
              completionPercentage: savedCompletion ?? current.completionPercentage,
              loadedAt: new Date().toISOString(),
            });
          });
          items.sort((a, b) => {
            const getDate = (x) =>
              x.lastUpdated?.toDate?.() ||
              (x.lastUpdated && new Date(x.lastUpdated)) ||
              (x.submittedAt && new Date(x.submittedAt)) ||
              (x.createdAt && new Date(x.createdAt)) ||
              new Date(0);
            return getDate(b) - getDate(a);
          });
          return items;
        }
        // localStorage fallback
        const out = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (
            key &&
            (key.startsWith("footballAnalysisData") ||
              key.startsWith("submitted_analysis"))
          ) {
            try {
              const data = JSON.parse(localStorage.getItem(key));
              const savedCompletion =
                data.completionPercentage || data.submissionCompletionPercentage;
              const current = calculateCompletion(data);
              out.push({
                id: key,
                ...data,
                completionPercentage: savedCompletion ?? current.completionPercentage,
                loadedFrom: "localStorage",
              });
            } catch {}
          }
        }
        return out;
      } catch (e) {
        console.error("❌ Load analyses error:", e);
        return [];
      }
    },
    [user]
  );

  // -----------------------------
  // Group operations (MAP members)
  // -----------------------------
  const createGroup = useCallback(
    async (groupData) => {
      try {
        const inviteCode = generateInviteCode();
        const nowISO = new Date().toISOString();
        const creator = user?.uid || "anonymous";

        const clean = {
          name: String(groupData.name || "").trim(),
          description: String(groupData.description || "").trim(),
          team: String(groupData.team || ""),
          teamId: String(groupData.teamId || ""),
          league: String(groupData.league || ""),
          conference: String(groupData.conference || ""),
          division: String(groupData.division || ""),
          season: Number(groupData.season) || getFootballSeasonYear(),
          fanType: String(groupData.fanType || "casual"),

          createdBy: creator,
          createdAt: nowISO,
          inviteCode,
          status: "active",

          members: {
            [creator]: {
              userId: creator,
              role: "owner",
              joinedAt:
                window.firebase?.firestore?.FieldValue?.serverTimestamp?.() || null,
              status: "joined",
            },
          },
          memberIds: [creator],
          memberCount: 1,
          lastUpdated: window.firebase?.firestore?.FieldValue?.serverTimestamp?.() || null,
        };

        if (window.firebase && window.db) {
          await window.db.collection("groups").doc(inviteCode).set(clean);
          logger.debug("✅ Group created (map members)");
          return { groupId: inviteCode, inviteCode, ...clean };
        }

        // Fallback
        localStorage.setItem(`sbe_group_${inviteCode}`, JSON.stringify(clean));
        return { groupId: inviteCode, inviteCode, ...clean };
      } catch (e) {
        console.error("❌ Error creating group:", e);
        throw e;
      }
    },
    [generateInviteCode, user]
  );

  const joinGroup = useCallback(
    async (inviteCode) => {
      try {
        const code = String(inviteCode || "").trim().toUpperCase();
        if (!code) throw new Error("Invite code required");

        if (window.firebase && window.db && user?.uid) {
          const ref = window.db.collection("groups").doc(code);
          const snap = await ref.get();
          if (!snap.exists) throw new Error("Group not found");

          await ref.update({
            [`members.${user.uid}`]: {
              userId: user.uid,
              email: user.email || null,
              joinedAt: window.firebase.firestore.FieldValue.serverTimestamp(),
              status: "joined",
            },
            memberCount: window.firebase.firestore.FieldValue.increment(1),
            lastUpdated: window.firebase.firestore.FieldValue.serverTimestamp(),
            memberIds: window.firebase.firestore.FieldValue.arrayUnion(user.uid),
          });

          const fresh = await ref.get();
          return { groupId: code, inviteCode: code, ...fresh.data() };
        }

        throw new Error("Firebase not available or user not authenticated");
      } catch (e) {
        console.error("❌ Error joining group:", e);
        throw e;
      }
    },
    [user]
  );

  // User groups listing (creator or member)
  const getUserGroups = useCallback(async () => {
    try {
      if (!(typeof window !== "undefined" && window.firebase && window.db && user?.uid)) return [];
      const uid = user.uid;
      const results = new Map();

      // Created groups
      try {
        const createdSnap = await window.db.collection('groups').where('createdBy', '==', uid).get();
        createdSnap.forEach(doc => results.set(doc.id, { id: doc.id, ...doc.data() }));
      } catch {}

      // Member via array field
      try {
        const memberSnap = await window.db.collection('groups').where('memberIds', 'array-contains', uid).get();
        memberSnap.forEach(doc => results.set(doc.id, { id: doc.id, ...doc.data() }));
      } catch {}

      // Member via map status
      try {
        const path = `members.${uid}.status`;
        const mapSnap = await window.db.collection('groups').where(path, '==', 'joined').get();
        mapSnap.forEach(doc => results.set(doc.id, { id: doc.id, ...doc.data() }));
      } catch {}

      const list = Array.from(results.values());
      list.sort((a,b) => {
        const da = (a.lastUpdated?.toDate?.() || (a.lastUpdated && new Date(a.lastUpdated)) || (a.createdAt && new Date(a.createdAt)) || new Date(0));
        const db = (b.lastUpdated?.toDate?.() || (b.lastUpdated && new Date(b.lastUpdated)) || (b.createdAt && new Date(b.createdAt)) || new Date(0));
        return db - da;
      });
      return list;
    } catch (e) {
      console.error('❌ getUserGroups error:', e);
      return [];
    }
  }, [user]);

  const updateGroup = useCallback(
    async (groupId, updateData) => {
      try {
        if (!groupId || !updateData || typeof updateData !== "object") {
          const err = new Error("Invalid group ID or update data");
          err.code = "invalid-argument";
          throw err;
        }
        const perform = async () => {
          if (!(window.firebase && window.db)) {
            const err = new Error("Firebase not available");
            err.code = "unavailable";
            throw err;
          }
          const ref = window.db.collection("groups").doc(groupId);
          const clean = { ...updateData };
          if ("members" in clean && typeof clean.members !== "object") delete clean.members;

          clean.updatedAt = window.firebase.firestore.FieldValue.serverTimestamp();
          clean.lastModifiedBy = user?.uid || "anonymous";
          Object.keys(clean).forEach((k) => {
            const v = clean[k];
            if (typeof v === "function" || v === undefined) delete clean[k];
          });

          await ref.update(clean);
          logger.debug("✅ Group updated");
        };
        return await withRetry(perform, 3, 1000);
      } catch (error) {
        console.error("❌ Firebase update failed:", error);
        // Fallback to localStorage
        try {
          const key = `sbe_group_${groupId}`;
          const prev = localStorage.getItem(key);
          const existing = prev ? JSON.parse(prev) : {};
          const merged = {
            ...existing,
            ...updateData,
            updatedAt: new Date().toISOString(),
            lastModifiedBy: user?.uid || "anonymous",
            fallbackUpdate: true,
          };
          localStorage.setItem(key, JSON.stringify(merged));
          logger.success("✅ Group updated in localStorage (fallback)");
        } catch (storageError) {
          const comprehensive = new Error("Failed to update group data");
          comprehensive.originalError = error;
          comprehensive.fallbackError = storageError;
          comprehensive.errorCategory = "storage";
          throw comprehensive;
        }
      }
    },
    [user]
  );

  // Submitted analyses for a group
  const getGroupResults = useCallback(async (groupId) => {
    try {
      logger.debug("📊 getGroupResults", { groupId });
      const perform = async () => {
        if (!groupId || typeof groupId !== "string") {
          const err = new Error("Valid group ID is required");
          err.code = "invalid-argument";
          throw err;
        }
        if (typeof window !== "undefined" && window.firebase && window.db) {
          const q = window.db
            .collection("gameAnalysis")
            .where("groupId", "==", groupId)
            .where("status", "==", "submitted");
          const snap = await q.get();
          const results = [];
          snap.forEach((doc) => {
            const data = doc.data();
            if (data && typeof data === "object") results.push({ id: doc.id, ...data });
          });
          return results;
        }
        // localStorage fallback
        const out = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("submitted_analysis_") && key.includes(groupId)) {
            try {
              const data = JSON.parse(localStorage.getItem(key));
              if (data?.groupId === groupId)
                out.push({ id: key, ...data, loadedFrom: "localStorage" });
            } catch {}
          }
        }
        return out;
      };
      return await withRetry(perform, 2, 1000);
    } catch (e) {
      console.error("❌ getGroupResults error:", e);
      e.errorCategory = categorizeFirebaseError(e);
      return [];
    }
  }, []);

  // -----------------------------
  // Connection monitoring
  // -----------------------------
  const monitorConnection = useCallback((callback) => {
    if (typeof callback !== "function") return () => {};
    logger.network?.("🔧 Setting up connection monitoring");

    let isOnline = navigator.onLine;
    let failures = 0;
    let interval = null;
    const OFFLINE_THRESHOLD = 3;
    const PERIOD = 30000;

    const check = async () => {
      try {
        if (typeof window !== "undefined" && window.firebase && window.db) {
          const start = Date.now();
          await window.db.collection("health").limit(1).get();
          const ms = Date.now() - start;
          failures = 0;
          if (!isOnline) {
            isOnline = true;
            callback({
              isOnline: true,
              firebaseConnected: true,
              timestamp: new Date().toISOString(),
              responseTime: ms,
            });
          }
        }
      } catch (err) {
        failures += 1;
        if (failures >= OFFLINE_THRESHOLD && isOnline) {
          isOnline = false;
          callback({
            isOnline: false,
            firebaseConnected: false,
            timestamp: new Date().toISOString(),
            error: err.message,
            consecutiveFailures: failures,
          });
        }
      }
    };

    const onOnline = () => setTimeout(check, 1000);
    const onOffline = () => {}; // let checker flip state

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    interval = setInterval(check, PERIOD);
    setTimeout(check, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const checkFirebaseHealth = useCallback(async () => {
    try {
      const health = {
        timestamp: new Date().toISOString(),
        browserOnline: navigator.onLine,
        firebaseAvailable: false,
        firestoreConnection: false,
        functionsAvailable: false,
      };
      if (typeof window !== "undefined" && window.firebase && window.db) {
        health.firebaseAvailable = true;
        try {
          const start = Date.now();
          await window.db.collection("health").limit(1).get();
          health.firestoreConnection = true;
          health.responseTime = Date.now() - start;
        } catch (err) {
          health.firestoreError = err.message;
        }
        if (window.functions) {
          try {
            health.functionsAvailable = true;
          } catch (err) {
            health.functionsError = err.message;
          }
        }
      }
      health.status = health.firestoreConnection ? "healthy" : "unhealthy";
      return health;
    } catch (e) {
      return {
        status: "unhealthy",
        error: e.message,
        errorCategory: categorizeFirebaseError(e),
        timestamp: new Date().toISOString(),
        browserOnline: navigator.onLine,
      };
    }
  }, []);

  const forceReconnect = useCallback(
    async () => {
      try {
        if (typeof window !== "undefined" && window.firebase && window.db) {
          await window.db.enableNetwork();
          const health = await checkFirebaseHealth();
          return health.status === "healthy";
        }
        return false;
      } catch (e) {
        logger.error?.("❌ Force reconnect failed:", e.message);
        return false;
      }
    },
    [checkFirebaseHealth]
  );

  // -----------------------------
  // expose
  // -----------------------------
  return useMemo(
    () => ({
      // data
      saveGameData,
      loadGameData,
      submitAnalysis,
      getUserAnalyses,

      // groups
      createGroup,
      joinGroup,
      updateGroup,
      getGroupResults,
      getUserGroups,

      // utils
      withRetry,
      categorizeFirebaseError,
      monitorConnection,
      checkFirebaseHealth,
      forceReconnect,

      sportsDataService,
      generateInviteCode,

      user,
      isAuthenticated: !!user,
    }),
    [
      saveGameData,
      loadGameData,
      submitAnalysis,
      getUserAnalyses,
      createGroup,
      joinGroup,
      updateGroup,
      getGroupResults,
      getUserGroups,
      monitorConnection,
      checkFirebaseHealth,
      forceReconnect,
      sportsDataService,
      generateInviteCode,
      user,
    ]
  );
};
